import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { InAppReview } from '@capacitor-community/in-app-review';
import { firstValueFrom } from 'rxjs';
import { PLATFORM_TOKEN, PlatformProvider } from '../tokens/platform.token';
import { RatingHttpService } from './rating-http.service';

const KEY_COMPLETED: string = 'splitzero.rating.completedSessions';
const KEY_RATED_AT: string = 'splitzero.rating.lastRatedAt';
const KEY_DISMISSED_AT: string = 'splitzero.rating.lastDismissedAt';

const DAY_MS: number = 24 * 60 * 60 * 1000;
// Próg: pokaż prompt po 5. ukończonej sesji. ADR-0011.
const SESSIONS_THRESHOLD: number = 5;
// Cooldown 60 dni po odrzuceniu ("Później").
const DISMISS_COOLDOWN_MS: number = 60 * DAY_MS;
// Po wystawieniu oceny pytamy ponownie dopiero po 6 miesiącach (~182 dni).
const RATE_REASK_MS: number = 182 * DAY_MS;
// Natywny prompt sklepu odpalamy tylko przy komplecie gwiazdek.
const STORE_REVIEW_RATING: number = 5;

@Injectable({ providedIn: 'root' })
export class AppRatingService {
  private readonly api: RatingHttpService = inject(RatingHttpService);
  private readonly platform: PlatformProvider = inject(PLATFORM_TOKEN);

  private readonly openSignal: WritableSignal<boolean> = signal<boolean>(false);
  readonly open: Signal<boolean> = this.openSignal.asReadonly();

  registerCompletedSessionAndMaybePrompt(): void {
    const completed: number = this.readNumber(KEY_COMPLETED) + 1;
    this.write(KEY_COMPLETED, String(completed));
    if (this.shouldPrompt(completed)) {
      this.openSignal.set(true);
    }
  }

  async submit(rating: number, comment: string | null): Promise<void> {
    const trimmed: string | null = comment?.trim() ? comment.trim() : null;
    await firstValueFrom(this.api.submit({ rating, comment: trimmed }));
    this.write(KEY_RATED_AT, String(Date.now()));
    this.openSignal.set(false);

    if (rating >= STORE_REVIEW_RATING && this.platform.isNativeRun) {
      try {
        await InAppReview.requestReview();
      } catch {}
    }
  }

  dismiss(): void {
    this.write(KEY_DISMISSED_AT, String(Date.now()));
    this.openSignal.set(false);
  }

  private shouldPrompt(completedSessions: number): boolean {
    if (completedSessions < SESSIONS_THRESHOLD) return false;
    const now: number = Date.now();
    const ratedAt: number = this.readNumber(KEY_RATED_AT);
    if (ratedAt > 0 && now - ratedAt < RATE_REASK_MS) return false;
    const dismissedAt: number = this.readNumber(KEY_DISMISSED_AT);
    return !(dismissedAt > 0 && now - dismissedAt < DISMISS_COOLDOWN_MS);
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private readNumber(key: string): number {
    const raw: string | null = this.read(key);
    const n: number = raw === null ? 0 : Number(raw);
    return Number.isFinite(n) ? n : 0;
  }

  private write(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // localStorage może być wyłączony (Safari private mode) - ignorujemy
    }
  }
}
